import { Info, Settings2 } from 'lucide-react';
import { type PowerInfo } from '@/lib/powers-data';
import {
  resolvePowerPstyleDialogMock,
  resolvePowerPstyleSectionCopy,
} from '@/lib/power-pstyle-cms';
import {
  PSTYLE_BONUS_GRADIENTS,
  PSTYLE_DEFAULT_GRADIENTS,
  powerShowsPstyleSection,
  resolvePowerPstyleTheme,
} from '@/lib/power-pstyle-themes';
import { GallerySectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import PowerProfileDialogMock, {
  XAT_PROFILE_DIALOG_WIDTH,
} from '@/components/help-center/powers/PowerProfileDialogMock';
import { PowerDescriptionContent } from '@/components/help-center/powers/PowerMentionText';
import { looksLikeHtml } from '@/lib/cms-html';

interface PowerPstyleSectionProps {
  power: PowerInfo;
}

export default function PowerPstyleSection({ power }: PowerPstyleSectionProps) {
  if (!powerShowsPstyleSection(power)) return null;

  const theme = resolvePowerPstyleTheme(power);
  const dialogMock = resolvePowerPstyleDialogMock(power);
  const copy = resolvePowerPstyleSectionCopy(power);
  const isBase = theme.kind === 'base';
  const title = copy.title.trim() || (isBase ? 'Pstyle' : `Tema Pstyle · ${theme.themeName}`);
  const requirement = copy.requirement.trim();
  const intro = copy.intro.trim();
  const tip = copy.tip.trim();

  return (
    <section className="border-b border-gray-100 bg-white py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-start">
          <div className="min-w-0">
            <div className="mb-8 flex items-center gap-3">
              <GallerySectionSmileyIcon code="pstyle" bgClass="bg-emerald-50" />
              <div>
                <h2 className="font-poppins text-xl font-bold text-gray-900">{title}</h2>
                {requirement ? (
                  <p className="mt-0.5 font-inter text-sm text-emerald-700">{requirement}</p>
                ) : null}
              </div>
            </div>

            <div className="space-y-4 font-inter text-base leading-relaxed text-gray-600">
              {intro ? (
                looksLikeHtml(intro) ? (
                  <PowerDescriptionContent text={intro} className="text-gray-600" />
                ) : (
                  <p className="whitespace-pre-line">{intro}</p>
                )
              ) : null}
            </div>

            {tip ? (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                <Settings2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                <div className="min-w-0 flex-1 font-inter text-sm leading-relaxed text-emerald-900">
                  {looksLikeHtml(tip) ? (
                    <PowerDescriptionContent text={tip} className="text-emerald-900" />
                  ) : (
                    <p className="whitespace-pre-line">{tip}</p>
                  )}
                </div>
              </div>
            ) : null}

            {isBase ? (
              <div className="mt-8 space-y-5">
                <div>
                  <h3 className="mb-2 font-poppins text-sm font-semibold text-gray-900">
                    Gradientes incluídos
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {PSTYLE_DEFAULT_GRADIENTS.map((name) => (
                      <span
                        key={name}
                        className="rounded-full bg-gray-100 px-3 py-1 font-poppins text-xs font-semibold text-gray-700 ring-1 ring-gray-200"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="mb-2 font-poppins text-sm font-semibold text-gray-900">
                    Temas bónus (requerem Pstyle + requisito)
                  </h3>
                  <ul className="space-y-1.5 font-inter text-sm text-gray-600">
                    {PSTYLE_BONUS_GRADIENTS.map((item) => (
                      <li key={item.name} className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800">{item.name}</span>
                        <span className="text-gray-400">·</span>
                        <span>{item.requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex min-w-0 justify-center">
            <div
              className="flex w-full flex-col items-center"
              style={{ maxWidth: XAT_PROFILE_DIALOG_WIDTH }}
            >
              <div className="mb-3 flex w-full items-center justify-center gap-2">
                <Info className="h-4 w-4 text-gray-400" aria-hidden />
                <p className="font-poppins text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Pré-visualização do Pstyle
                </p>
              </div>

              <PowerProfileDialogMock mock={dialogMock} className="w-full" />

              <p className="mt-4 w-full text-center font-inter text-[11px] leading-relaxed text-gray-400">
                Os botões na pré-visualização do xat são apenas decorativos — a personalização é feita
                no separador Pstyle das configurações.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
